import { forwardRef, useMemo } from 'react';

import { useTheme } from '@mui/material';

import { Drawer, Icons, Stack } from '@cast/design-system';

import { CloseDrawerButton } from 'components/drawer/ds';
import { notReachable } from 'utils/types/not-reachable';

import { NodeWithDependencies } from '../types';

import { ChipSearch } from '@cast/shared-search-ui';

export type NodeAttribute = keyof Pick<
  NodeWithDependencies,
  'labels' | 'annotations' | 'taints'
>;

type Props = {
  nodeAttribute: NodeAttribute;
  node: NodeWithDependencies;
};

const NoDataIcon = ({ nodeAttribute }: { nodeAttribute: NodeAttribute }) => {
  const { palette } = useTheme();

  switch (nodeAttribute) {
    case 'labels':
      return <Icons.TagSimple weight="fill" color={palette.grey[400]} />;
    case 'annotations':
      return <Icons.BookmarksSimple weight="fill" color={palette.grey[400]} />;
    case 'taints':
      return <Icons.PaintRoller weight="fill" color={palette.grey[400]} />;
    default:
      return notReachable(nodeAttribute);
  }
};

const formatTaints = (node: NodeWithDependencies) => {
  const taints = node.taints || [];
  return taints
    .map(({ key, value, effect }) => `${key}=${value}:${effect}`)
    .sort();
};

const formatLabelsOrAnnotations = (
  node: NodeWithDependencies,
  nodeAttribute: Extract<NodeAttribute, 'labels' | 'annotations'>
) => {
  const labelsOrAnnotations = node[nodeAttribute] || {};
  return Object.entries(labelsOrAnnotations).map(
    ([key, value]) => `${key}=${value}`
  );
};

export const NodeAttributesDrawer = forwardRef<HTMLDivElement, Props>(
  ({ node, nodeAttribute, ...props }: Props, ref) => {
    const data = useMemo(() => {
      switch (nodeAttribute) {
        case 'taints':
          return formatTaints(node);
        case 'labels':
        case 'annotations':
          return formatLabelsOrAnnotations(node, nodeAttribute);
        default:
          return notReachable(nodeAttribute);
      }
    }, [node, nodeAttribute]);

    return (
      <Drawer ref={ref} {...props} color="secondary" size="medium">
        <Stack gap="24px" p="24px 32px" data-testid="node-attributes-drawer">
          <CloseDrawerButton
            positioned={false}
            sx={{ alignSelf: 'flex-end', color: 'grey.500' }}
            icon="x"
          />

          <ChipSearch
            entity={nodeAttribute}
            components={{
              noDataIcon: <NoDataIcon nodeAttribute={nodeAttribute} />,
            }}
            data={data}
          />
        </Stack>
      </Drawer>
    );
  }
);

NodeAttributesDrawer.displayName = 'NodeAttributesDrawer';
